import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { user } from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import moment from 'moment/moment';
import { calculateArrivalDate, getAdmissionStartDate } from '../../../../util/patientUtil';
import { getConfig } from '../../../../util/configUtil';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dischargeDestination, dischargeFollowUp, dischargeStatus, getEcdsVersion } from '../../../../store/reducers/dicts';
import useOpenState from '../../../../hooks/useOpenState';
import { diagnosisBase } from '../clerkingED/clerking';
import { triageBase } from '../triage/triageUtils';
import { CLINICAL_COURSE_BASE, CLINICAL_COURSE_OPTIONS, getSafeguardingValues } from '../formsUtil';
import API from '../../../../app/api';
import { addOrReplace, getPatientFlagValue, isFlagDisabled } from '../../mainScreen/util/patientFlagsUtil';
import APIForm from '../../../../app/apiForm';
import { setSnackbar } from '../../../../store/reducers/globals';
import Notes from '../../../../util/notes';
import keyPerformanceIndicator from '../../../../util/timeMarker';
import { UtilsMomentJS } from '../../../../store/utilsMoment';
import { Button, DialogContent, Divider, FormLabel, Grid, Typography } from '@mui/material';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import InformationBox from '../../../../components/other/informationBox';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import { DelayReason } from '../../../../controls/agyle/dictionaries';
import { ControlDiagnosis, ControlInvestigations, ControlTreatment } from '../../../../controls/agyle/diagnosis';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import clsx from 'clsx';
import { ChiefOfComplaint, DischargeDestination, DischargeFollowUp, DischargeStatus, Safeguarding } from '../../../../controls/agyle/ecds';
import AgyleDictionaryField from '../../../../components/ui/selector/agyleDictionaryField';
import AgyleLoadingSpinner from '../../../../components/other/agyleLoadingSpinner';
import {
    checkCauseForConcernForm,
    checkCFSForm,
    checkSafeguardingLevelForm,
    getSafeguardingLevel,
    PATIENT_CONTEXT_TYPE,
} from '../../../../util/patientContextUtil';
import { setWorkflow } from '../../../../store/reducers/workflow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import DiseaseOutbreak from '../ecds/diseaseOutbreak';
import DraggableModal from '../../../../components/modal/DraggableModal';
import AgyleSelectField from '../../../../components/ui/selector/agyleSelectField';
import AgyleSwitchCalendar from '../../../../components/calendar/agyleSwitchCalendar';
import MessageBox from '../../../../util/messageBox';
import makeStyles from '@mui/styles/makeStyles';
import { WORKFLOW_OPTIONS } from '../../../../util/workflowUtil';
import { closePatientForm, ctxDischargeTemplate, ctxPatientType } from '../../../../store/reducers/patientContext';
import SchedulerModal from '../scheduler/schedulerModal';
import AppointmentsAccordion from './appointmentsAccordion';
import { wardMapObject } from '../../../../store/reducers/wardMap';
import { consultantDictionary } from '../../../../controls/agyle/clinician/clinitianUtils';
import ControlConsultantComponent from '../../../../controls/agyle/clinician/controlConsultantComponent';

const EdReviewDischarge = React.lazy(() => import('../dischargeContext/edReviewDischarge'));

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
    },
    dialogContent: {
        height: 'auto',
    },
    button: {
        background: '#fff',
        color: '#000',
        '&:hover': {
            background: '#fff',
        },
    },
    clinicalCourseAuthor: {
        float: 'right',
        fontSize: 'small',
        fontStyle: 'italic',
    },
    outcome: {
        borderRadius: '7px',
        border: '1px solid #7199E6',
        background: '#cfdffd',
        padding: 10,
        color: '#3F73D6',
        marginBottom: '10px',
    },
    paper: {
        background: theme.palette.background.paper,
        padding: '0 10px 10px 10px',
    },
    input: {
        width: '100%',
    },
    medical: {
        margin: 5,
    },
    select: {
        float: 'right',
        width: 150,
        marginBottom: 5,
    },
    accordion: {
        marginTop: 20,
        backgroundColor: theme.palette.accordion.backgroundColor,
    },
    heading: {
        fontSize: '20px',
    },
    error: {
        color: '#f44336',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    center: {
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    infoBoxForm: {
        marginLeft: 15,
        marginTop: 10,
        width: '100%',
    },
    picker: {
        backgroundColor: theme.palette.background.paper,
    },
    flagContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    flagTitle: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 150,
        gap: 5,
        fontWeight: 'bold',
        padding: '1px 0px 1px 5px',
    },
}));

const dischargeGroup = ['Discharged', 'Ambulatory / short stay', 'Transfer'];

const DischargeForm = (props) => {
    const { t } = useTranslation();
    const classesLocal = useStyles();
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const { destination } = props;

    const dispatch = useDispatch();

    // Selectors
    const mapObject = useSelector(wardMapObject);

    const age = moment(getAdmissionStartDate(props.patient?.admission)).diff(props.patient?.patient?.dob, 'years');

    // Configs
    const postEditDischargeForm = getConfig('postEditDischargeForm', false);
    const bedasEnabled = getConfig('modules.bedas.enabled', false);
    const schedulerEnabled = getConfig('modules.scheduler.enabled', false) && getConfig('modules.scheduler.buttonInDischarge', false);
    const formCheckEnabled = getConfig('dischargeFollowUpCheck.enabled', false);
    const fractureClinicCode = getConfig('dischargeFollowUpCheck.fractureClinic', null);
    const fractureClinicFromId = getConfig('dischargeFollowUpCheck.fractureClinicFormId', null);
    const physiotherapyCode = getConfig('dischargeFollowUpCheck.physiotherapy', null);
    const mandatoryFollowUp = getConfig('dischargeFollowUpCheck.mandatory', false);
    const nokQuestionEnabled = getConfig('modules.forms.discharge.enableDestinationNokQuestion', false);
    const nokQuestionCodes = getConfig('modules.forms.discharge.nokQuestionDestinationsCodes', []);
    const disablePrepopulationDischarge = getConfig('disablePrepopulationDischarge', false);
    const dischargeTimestampAdditionalInfo = getConfig('dischargeTimestampAdditionalInfo', false);
    const verificationOfDeathAgyleMiniForm = getConfig('verificationOfDeathAgyleMiniForm', false);
    const diseaseInDischarge = getConfig('diseaseInDischarge', false);
    const disableFirstClinicallyReadyToProceedDischarge = getConfig('disableFirstClinicallyReadyToProceedDischarge', false);
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const ecdsDateFormat = getConfig('ecdsDateFormat');
    const ecdsTimeFormat = getConfig('ecdsTimeFormat');
    const showMidnightWarningInDischarge = getConfig('showMidnightWarningInDischarge', false);
    const client = getConfig('client', null);
    const safeguardingFormModalInDis = getConfig('safeguardingFormModalInDis', false);
    const safeguardingFormLink = getConfig('safeguardingFormLink', '');
    const causeForConcernFormLink = getConfig('causeForConcernFormLink', '');
    const triageMandatoryToDischarge = getConfig('triageMandatoryToDischarge', false);
    const autoTransfereFromDrafts = getConfig('autoTransfereFromDrafts', false);
    const isUHD = client === 'UHD';

    const controller = new AbortController();
    const isMounted = React.useRef(true);
    const [selfDischardeAlertVisable, setSelfDischardeAlertVisable] = useState(false);

    const patientFromDorset = () => {
        if (props.patient?.patient?.addressLines && _.isArray(props.patient?.patient?.addressLines)) {
            return props.patient.patient?.addressLines.findIndex((address) => address?.trim()?.toLowerCase() === 'dorset') !== -1;
        }
        return false;
    };

    // Form Data
    const [dataForm, setDataForm] = useState({
        who: null,
        delayReasons: {},
        departureDate: moment(),
        departureTime: moment(),
        departureDateAndTime: moment(),
        dischargeDestination: null,
        dischargeStatus: null,
        edSisters: false,
        chadOrCamhs: false,
        type: props.dialogType === 'dischargeEDAU' ? 'edau' : 'ed',
        nokContacted: null,
        nokContactedReason: '',
    });
    const [dataReadOnly, setDataReadOnly] = useState({});
    const [isDischargeSaved, setIsDischargeSaved] = useState(false);

    const [outbreak, setOutbreak] = useState({});

    // validation
    const [errorPicker, setErrorPicker] = useState({
        date: null,
        time: null,
    });

    // Dictionaries
    const [hospitals, setHospitals] = useState([]);
    const [wards, setWards] = useState([]);
    const dischargeStatusDict = useSelector(dischargeStatus);
    const dischargeDestinationDict = useSelector(dischargeDestination);
    const dischargeFollowUpDict = useSelector(dischargeFollowUp);
    const dischargeTemplate = useSelector(ctxDischargeTemplate);
    const version = dispatch(getEcdsVersion(props.patient?.admission?.startDate));
    const patientType = useSelector(ctxPatientType);

    // Delay Time
    const [delayTime, setDelayTime] = useState(moment.duration(moment().diff(calculateArrivalDate(props.patient))).asHours());

    // Accordion
    const [expanded, setExpanded] = React.useState(false);

    // BEDAS
    const [bedas, setBedas] = useState(false);
    const [bedasDate, setBedasDate] = useState(null);
    const [bedasReason, setBedasReason] = useState(null);
    const [bedasSessionId, setBedasSessionId] = useState(null);
    const bedasTouch = useRef(false);
    const [events, setEvents] = useState([]);
    const [isCalendarOpen, openCalendar, closeCalendar] = useOpenState(false, false);
    const [isBookingOpen, openBooking, closeBooking] = useOpenState(false, true);
    const [isSchedulerOpen, openScheduler, closeScheduler] = useOpenState(false, true);

    // Flags
    const firstUpdateFlag = useRef(false);

    const [flags, setFlags] = useState({
        res: getPatientFlagValue(props.patient, 'res'),
        maj: getPatientFlagValue(props.patient, 'maj'),
        amb: getPatientFlagValue(props.patient, 'amb'),
        min: getPatientFlagValue(props.patient, 'min'),
        utc: getPatientFlagValue(props.patient, 'utc'),
        rats: getPatientFlagValue(props.patient, 'rats'),
        children: getPatientFlagValue(props.patient, 'children'),
    });

    // Form check
    const [loadingForms, setLoadingForms] = useState(false);
    const [formCheck, setFormCheck] = useState(null);
    const [formNotRequired, setFormNotRequired] = useState(false);

    // NoK contacted question
    const [nokQuestionShow, setNokQuestionShow] = useState(false);
    const [nokReason, setNokReason] = useState('');
    const [isNoKReasonOpen, openNoKReason, closeNoKReason] = useOpenState(false, true);

    const isDisabled =
        postEditDischargeForm && !props.isLocked
            ? props.isDischargeDisabled
            : props.disabled || props.patient?.admission?.status === 'DISCHARGE_REQUESTED';

    const [isWarning, openWarning, closeWarning] = useOpenState(false, true);

    // Bed Request
    const [isBedRequestInfoOpen, openBedRequestInfo, closeBedRequestInfo] = useOpenState(false, true);

    // Note modal
    const [isModalOpen, openModal, closeModal] = useOpenState(false, true);

    const noteTextRef = React.useRef(null);
    const [noteTextBounds, setNoteTextBounds] = React.useState({
        left: -(window.innerWidth - 830),
        right: 0,
        top: 0,
        bottom: window.innerHeight - 570,
    });

    // others
    const [diagnosis, setDiagnosis] = React.useState(props.patient.diagnosis ? props.patient.diagnosis : diagnosisBase());
    const [triage, setTriage] = React.useState(props.patient?.triage ? props.patient?.triage : triageBase());
    const [clinicalCourse, setClinicalCourse] = React.useState(
        props.patient?.studies?.clinicalCourse ? props.patient?.studies?.clinicalCourse : CLINICAL_COURSE_BASE(),
    );

    const [safeguardingList, setSafeguardingList] = React.useState([]);

    const arrivalDate = calculateArrivalDate(props.patient);
    const [schedulerHour, setSchedulerHour] = useState(null);

    const [loggedClinician, setLoggedClinician] = useState(null);

    useEffect(() => {
        API.getDictByURL(`/$dict/DischargeClinicians`)
            .then((dict) => {
                const clinicianObject = dict?.__data?.find((c) => c.userName === userObject.userName && c.deactivated === false);
                setLoggedClinician(clinicianObject || null);
            })
            .catch(console.error);
    }, [userObject.userName]);

    useEffect(async () => {
        let cancel = false;
        await initData();
        await API.getHospitals('forDischarge eq 1 or forDischarge eq true').then((response) => {
            if (cancel) return;
            if (response?.data?.__data) {
                setHospitals(response?.data?.__data);
            }
        });

        await API.getWards().then((response) => {
            if (cancel) return;
            if (response?.data?.__data) {
                setWards(response?.data?.__data);
            }
        });

        if (!props.patient?.studies?.discharge && !props.value) {
            let time = moment();

            let status = null;
            let destinations = null;
            let followUp = null;
            let departureDate = null;
            let departureTime = null;
            let clinician = null;
            let delayReasons = {
                primaryDelayReason: null,
                secondaryDelayReason: null,
                delayReasonText: '',
            };

            if (getPatientFlagValue(props.patient, 'deceased')) {
                status = _.find(dischargeStatusDict, { ecdsVersion: version, snomedCode: '75004002' });
                destinations = _.find(dischargeDestinationDict, { ecdsVersion: version, snomedCode: '305398007' });
            } else if (!disablePrepopulationDischarge) {
                status = _.find(dischargeStatusDict, { ecdsVersion: version, snomedCode: '182992009' });
                if (props.populateDestination)
                    destinations = _.find(dischargeDestinationDict, {
                        ecdsVersion: version,
                        snomedCode: props.edau ? '1066331000000109' : '306689006',
                    });
            }

            if (!disablePrepopulationDischarge)
                followUp = _.find(dischargeFollowUpDict, {
                    ecdsVersion: version,
                    snomedCode: '3780001',
                });

            if (bedasEnabled && _.toLower(props.patient?.metadata?.diagnosisNotRequired?.reason?.__description) === 'bedas') {
                followUp = _.find(dischargeFollowUpDict, { ecdsVersion: version, snomedCode: '1077181000000100' }); // Only for ECDS V3
                setBedas(true);
            }

            if (props.patient?.metadata?.dischargeTimeMarker) {
                time = moment(props.patient?.metadata?.dischargeTimeMarker);
            }

            if (dischargeTimestampAdditionalInfo && props.patient?.metadata?.dischargeTimestampData) {
                const timestamp = props.patient?.metadata?.dischargeTimestampData;
                status = timestamp.dischargeStatus || status;
                destinations = timestamp.dischargeDestination || destinations;
                followUp = timestamp.dischargeFollowUp || followUp;
                departureDate = timestamp.departureDate ? moment(timestamp.departureDate) : departureDate;
                departureTime = timestamp.departureTime ? moment(timestamp.departureTime) : departureTime;
                clinician = timestamp.who;
            }

            if (verificationOfDeathAgyleMiniForm && props.patient?.studies?.verificationOfDeath) {
                const vod = props.patient?.studies?.verificationOfDeath;
                departureDate = vod.dateOfDeath ? moment(vod.dateOfDeath) : departureDate;
                departureTime = vod.dateOfDeath ? moment(vod.timeOfDeath) : departureTime;
            }

            if (dischargeTemplate) {
                delayReasons = {
                    primaryDelayReason: dischargeTemplate.delayReason || null,
                    secondaryDelayReason: dischargeTemplate.delayReasonSecondary || null,
                    delayReasonText: dischargeTemplate.delayReasonText || '',
                };
                if (dischargeTemplate.clinicianOption === 'setLogged') {
                    clinician = loggedClinician;
                } else if (dischargeTemplate.clinicianOption === 'setBlank') {
                    clinician = '';
                } else if (dischargeTemplate.clinicianOption === 'setClinician') {
                    clinician = dischargeTemplate.clinician;
                }
                status = dischargeTemplate.dischargeStatus || status;
                destinations = dischargeTemplate.dischargeDestination || destinations;
                followUp = dischargeTemplate.dischargeFollowUp || followUp;
            }

            if (!clinician && !dischargeTemplate) {
                let newFilter = `deactivated ne true and userName eq '${userObject.userName}'`;
                await API.getDictByURL(`/$dict/DischargeClinicians?$filter=${encodeURIComponent(newFilter)}`).then((dictData) => {
                    if (!isMounted) return;

                    if (!_.isEmpty(dictData?.__data)) {
                        clinician = dictData?.__data[0];
                    }
                });
            }

            setDataForm((prevState) => ({
                ...prevState,
                dischargeDestination: destinations || prevState.dischargeDestination,
                dischargeStatus: status || prevState.dischargeStatus,
                dischargeFollowUp: followUp || prevState.followUp,
                departureDate: departureDate || time,
                departureTime: departureTime || time,
                who: clinician || prevState.who,
                delayReasons: delayReasons || prevState.delayReasons,
            }));
        } else if (props.patient?.studies?.discharge) {
            bedasTouch.current = true;
        }

        return () => {
            cancel = true;
        };
    }, [dischargeTemplate, loggedClinician]);

    useEffect(() => {
        const safeguardingFinal = dischargeTemplate
            ? _.uniqWith([...getSafeguardingValues(props.patient), ...(dischargeTemplate.dischargeSafeguarding ?? [])], _.isEqual)
            : getSafeguardingValues(props.patient);

        setSafeguardingList(safeguardingFinal);
    }, [props.patient, dischargeTemplate]);

    useEffect(() => {
        getForms();
    }, []);

    useEffect(() => {
        const followUpController = new AbortController();
        const code = dataForm?.dischargeFollowUp?.snomedCode;

        // Check if bedas
        if (!bedasTouch.current && bedasEnabled) setBedas(code === '1077181000000100'); // Only for ECDS V3

        if (nokQuestionEnabled && nokQuestionShow) {
            handleDisableDischargeSubmitButton(
                _.isNull(dataForm?.nokContacted) || (dataForm?.nokContacted === false && _.isEmpty(dataForm?.nokContactedReason)),
            );
        } else if (formCheckEnabled && code === physiotherapyCode && formNotRequired === false) {
            handleDisableDischargeSubmitButton(true);
        } else if (formCheckEnabled && code === fractureClinicCode && formNotRequired === false) {
            setLoadingForms(true);
            const obj = {
                forms: [fractureClinicFromId],
                patient: props.patient?.patient?.__id,
                attendanceId: props.patient?.admission?.attendanceId,
            };

            // Disable submit
            handleDisableDischargeSubmitButton(true);

            APIForm.getCompletedForms(obj)
                .then((res) => {
                    if (!_.isEmpty(res?.forms?.[0]?.forms)) {
                        setFormCheck(_.last(res?.forms?.[0]?.forms));
                        handleDisableDischargeSubmitButton(false);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    dispatch(setSnackbar({ type: 'error', message: 'An error occurred while loading completed forms.' }));
                    setFormCheck(null);
                })
                .finally(() => {
                    setLoadingForms(false);
                });
        } else {
            handleDisableDischargeSubmitButton(false);
        }
        return () => {
            followUpController.abort();
        };
    }, [dataForm?.dischargeFollowUp, dataForm?.nokContacted, dataForm?.nokContactedReason, nokQuestionShow, formNotRequired]);

    // Check discharge destination for nok question
    useEffect(() => {
        if (nokQuestionEnabled && dataForm?.dischargeDestination) {
            // Check if discharge destination snomed code is in config array
            setNokQuestionShow(nokQuestionCodes?.includes(dataForm?.dischargeDestination?.snomedCode));
        }
    }, [dataForm?.dischargeDestination]);

    // Check for opened bed request
    useEffect(() => {
        if (!isDisabled && !_.isEmpty(props.patient?.bedRequests) && !props.isTransfer) {
            const find = _.find(props.patient?.bedRequests, (br) => !br?.followUp && br?.status === 'NEW' && !br?.canceled.checked);
            if (find) {
                openBedRequestInfo();
            }
        }
    }, [props.patient, isDisabled]);

    const getAttendanceIdsList = () => {
        let attendanceIds = [props.patient.admission.attendanceId];
        if (props.patient.studies?.edau?.transfer?.edAttendanceId && autoTransfereFromDrafts) {
            attendanceIds.push(props.patient.studies.edau.transfer.edAttendanceId);
        }
        return attendanceIds;
    };

    const getForms = async () => {
        let episodeNumber = null;

        if (autoTransfereFromDrafts && props.patient.admission.attendanceType !== 'E')
            episodeNumber = props.patient.admission?.unityEpisodeNumber;

        let t = await API.getFormSet(controller.signal);
        const selfDischargeFormSet = _.find(t, ['template', 'self.discharge']);
        if (!selfDischargeFormSet) return;

        APIForm.getFormList(
            props.patient.patient.__id,
            getAttendanceIdsList(),
            episodeNumber,
            [selfDischargeFormSet],
            controller.signal,
        ).then(
            (list) => {
                if (!isMounted.current) return;
                let selfDischargeForms = list[0]?.forms;

                if (selfDischargeForms?.length === 0) {
                    setSelfDischardeAlertVisable(false);
                } else if (selfDischargeForms?.length > 0) {
                    _.forEach(selfDischargeForms, (form) => {
                        if (form.__isClosed) setSelfDischardeAlertVisable(true);
                    });
                }
            },
            (er) => {
                dispatch(setSnackbar({ type: 'error', message: 'The form list is unavailable.' }));
                console.error(er);
                setLoadingForms(false);
            },
        );
    };

    const createObjectClinicalCourse = (date) => {
        return {
            who: userFullName,
            whoLogin: userObject?.userName,
            when: date,
            clinicalCourse: clinicalCourse.clinicalCourse,
            actionsGivenStatus: clinicalCourse.actionsGivenStatus,
            actionsGiven: clinicalCourse.actionsGiven,
            adviceGiven: clinicalCourse.adviceGiven,
        };
    };

    const createDiseaseOutbreak = (date) => {
        if (
            outbreak?.ClinicalTrialIdentifier === '' &&
            outbreak?.DiseaseOutBreakNotificationDescription === '' &&
            outbreak?.DiseaseOutBreakNotificationSnomedCt === ''
        )
            return null;

        return {
            who: userFullName,
            whoLogin: userObject?.userName,
            when: date,
            ...outbreak,
        };
    };

    const getChangedFlags = () => {
        if (flags) {
            const flagTime = moment().toISOString();
            const tester = Formatter.formatUserFullName(userObject);

            const res = { name: 'res', checked: flags.res, by: tester, date: flagTime };
            const maj = { name: 'maj', checked: flags.maj, by: tester, date: flagTime };
            const amb = { name: 'amb', checked: flags.amb, by: tester, date: flagTime };
            const min = { name: 'min', checked: flags.min, by: tester, date: flagTime };
            const children = { name: 'children', checked: flags.children, by: tester, date: flagTime };
            const utc = { name: 'utc', checked: flags.utc, by: tester, date: flagTime };
            const rats = { name: 'rats', checked: flags.rats, by: tester, date: flagTime };

            let clone = [];

            if (!_.isEmpty(props.patient?.metadata?.patientFlags)) {
                clone = _.cloneDeep(props.patient.metadata.patientFlags);
            }

            addOrReplace(clone, maj);
            addOrReplace(clone, res);
            addOrReplace(clone, min);
            addOrReplace(clone, amb);
            addOrReplace(clone, children);
            addOrReplace(clone, utc);
            addOrReplace(clone, rats);

            return { name: 'metadata.patientFlags', value: clone };
        }
    };

    useEffect(() => {
        calculateDelayTime(dataForm);
        const dateOfForm = moment().toISOString();
        const dischargeValue = createObject(dateOfForm);
        const code = dataForm?.dischargeFollowUp?.snomedCode;

        const saveObject = [
            {
                name: 'studies.discharge',
                noteType: 'studies.discharge',
                note: getNote(),
                value: dischargeValue,
                validation: validation(),
                isReadyToAdmit: props.patient?.clinicalConclusion?.status === 'Admitted',
                timestamp: props.isActionMode,
                bedas,
                deceased: getPatientFlagValue(props.patient, 'deceased'),
                destination: destination ?? null,
            },
        ];

        if (
            formNotRequired !== false &&
            formCheckEnabled &&
            formCheckEnabled &&
            (code === physiotherapyCode || (formCheckEnabled && code === fractureClinicCode))
        ) {
            saveObject.push({
                noteType: 'studies.discharge',
                note: Notes.getDischargeFormNotRequiredNote(formNotRequired, dischargeValue),
            });
        }

        const clinicalCourse = createObjectClinicalCourse(dateOfForm);
        if (
            clinicalCourse.actionsGiven !== '' ||
            clinicalCourse.actionsGivenStatus !== '' ||
            clinicalCourse.adviceGiven !== '' ||
            clinicalCourse.clinicalCourse !== ''
        ) {
            saveObject.push({
                name: 'studies.clinicalCourse',
                value: clinicalCourse,
            });
        }

        if (!_.isEmpty(safeguardingList)) {
            saveObject.push({
                name: 'studies.mainScreenSafeguarding',
                value: safeguardingList,
            });
        }

        if (!props.hideDiagTreatInv) {
            saveObject.push({
                name: 'diagnosis',
                value: diagnosis,
            });

            saveObject.push({
                name: 'triage',
                value: triage,
            });
        }

        if (bedasEnabled && bedas) {
            saveObject.push({
                name: 'bedas.status',
                value: 'started',
            });
            saveObject.push({
                name: 'bedas.date',
                valueDate: bedasDate,
            });
            saveObject.push({
                name: 'bedas.reason',
                value: bedasReason,
            });
            saveObject.push({
                name: 'bedas.cancelled',
                value: false,
            });

            if (bedasSessionId) {
                saveObject.push({
                    name: 'bedas.assignedSession',
                    value: bedasSessionId,
                });
            }
        }

        if (firstUpdateFlag.current) {
            const fls = getChangedFlags();

            if (fls?.name && fls?.value) {
                saveObject.push({ name: fls.name, value: fls.value });
            }
        }

        const diseaseOutbreakVal = createDiseaseOutbreak(dateOfForm);
        if (diseaseInDischarge && diseaseOutbreakVal) {
            saveObject.push({
                name: 'studies.diseaseOutbreak',
                value: diseaseOutbreakVal,
                noteType: 'studies.diseaseOutbreak',
                note: Notes.getDiseaseOutbreakNote(diseaseOutbreakVal),
            });
        }

        if (!disableFirstClinicallyReadyToProceedDischarge) {
            keyPerformanceIndicator(saveObject, props.patient, ['firstClinicallyReadyToProceedSaved'], {
                when: dischargeValue.departureDateAndTime,
                who: dischargeValue.who,
            });
        }
        props.onChange(saveObject);
    }, [
        dataForm,
        diagnosis,
        triage,
        clinicalCourse,
        outbreak,
        bedasDate,
        bedas,
        bedasReason,
        errorPicker,
        formNotRequired,
        destination,
        firstUpdateFlag.current,
        flags,
    ]);

    const initData = async () => {
        let discharge;
        if (props.patient?.studies?.discharge || props.value) {
            discharge = props.value ? props.value : props.patient?.studies?.discharge;

            let check = true;
            setFormNotRequired(discharge?.formNotRequired ?? false);
            if (discharge?.formNotRequired !== false) {
                handleDisableDischargeSubmitButton(false);
            }
            if (props.dialogType === 'dischargeEDAU') {
                check = discharge?.type === 'edau';
            }

            if (discharge?.scheduledDateAndTime) {
                setSchedulerHour(discharge.scheduledDateAndTime);
            }

            if (check) {
                setIsDischargeSaved(discharge.status !== 'DRAFT');
                // Crate local date from UTC date
                const dateUTC = UtilsMomentJS.getLocalDateFromUTCECDSFormat(discharge.departureDate, discharge.departureTime);

                discharge = {
                    ...discharge,
                    departureDate: dateUTC,
                    departureTime: dateUTC,
                    delayReasons: { ...discharge.delayReasons },
                };
            } else {
                discharge = {
                    clinicalConclusion: props.patient?.clinicalConclusion?.when
                        ? moment(props.patient?.clinicalConclusion?.when, defaultDateTimeFormat).isValid()
                            ? moment(props.patient.clinicalConclusion.when, defaultDateTimeFormat)
                            : moment(props.patient?.clinicalConclusion?.when)
                        : null,
                };
            }
        }

        if (props.patient.admission?.endDate) {
            discharge = {
                ...discharge,
                departureDate: moment(props.patient.admission?.endDate),
                departureTime: moment(props.patient.admission?.endDate),
            };
        }

        setDataForm((prevState) => ({ ...prevState, ...discharge }));
        if (discharge?.status !== 'DRAFT' && discharge?.status !== 'timestamp' && !props.value && !postEditDischargeForm) {
            setDataReadOnly((prevState) => ({ ...prevState, ...discharge }));
        }
    };

    const createObject = (date) => {
        let data = _.cloneDeep(dataForm);

        // Get date and time from pickers and create a UTC date. Remove second from time,
        const dateUTC = UtilsMomentJS.getUTCDateFromECDSFormat(data.departureDate, data.departureTime);

        data = {
            ...data,
            departureDate: dateUTC.format(ecdsDateFormat),
            departureTime: dateUTC.format(ecdsTimeFormat),
            departureDateAndTime: moment(
                moment(data.departureDate).format(ecdsDateFormat) +
                    'T' +
                    moment(data.departureTime).set({ second: 0 }).format(ecdsTimeFormat),
            ).toISOString(),
            delayReasons: { ...dataForm.delayReasons },
            date: date,
            by: userFullName,
            login: userObject?.userName,
            status: props.isActionMode ? 'timestamp' : null,
            scheduledDateAndTime: schedulerHour || null,
            formNotRequired,
            dischargeTemplate: dischargeTemplate ?? null,
        };
        return data;
    };

    const validation = () => {
        const patientIsDischarged =
            !!props.patient?.admission?.endDate &&
            (props.patient?.admission?.status === 'DISCHARGE_REQUESTED' || props.patient?.admission?.status === 'TRANSFER_REQUESTED');
        const templateDiagnosisNotReq = dischargeTemplate?.diagnosisRequired;
        const followUp = mandatoryFollowUp ? dataForm.dischargeFollowUp : true;
        let valid =
            dataForm.departureDate &&
            dataForm.departureTime &&
            dataForm.dischargeDestination &&
            dataForm.dischargeStatus &&
            followUp &&
            dataForm.who;

        if (
            errorPicker.date ||
            errorPicker.time ||
            !moment(dataForm.departureDate).isValid() ||
            !moment(dataForm.departureTime).isValid()
        ) {
            return 'Invalid Departure Date or Time';
        }

        if (
            !patientIsDischarged &&
            !templateDiagnosisNotReq &&
            !props.hideDiagTreatInv &&
            (_.isEmpty(diagnosis?.primary) || _.isEmpty(triage?.treatmentAndManagementPlan) || _.isEmpty(triage?.investigations)) &&
            !props.patient?.metadata?.diagnosisNotRequired?.acknowledge
        ) {
            return 'At least one Diagnosis, Treatment, Investigation must be selected';
        }

        if (props.dialogType === 'dischargeEDAU') {
            if (
                dischargeGroup.includes(dataForm.dischargeDestination?.group) &&
                (!dataForm.safeHomeEnv || !dataForm.mobility || !dataForm.analgesia || !dataForm.eds || !dataForm.leaflets)
            ) {
                return 'Not all fields in Medical Discharge Checklist have been checked';
            }

            if (
                dischargeGroup.includes(dataForm.dischargeDestination?.group) &&
                (!dataForm.cannula ||
                    !dataForm.transport ||
                    !dataForm.tto ||
                    !dataForm.homeAccess ||
                    !dataForm.carerNok ||
                    (dataForm.transport === 'yes' && !dataForm.transportDescription))
            ) {
                return 'Not all fields in Instructions for Discharging Staff Member have been checked';
            }

            if (dataForm.dischargeDestination?.group === 'Admitted' && !dataForm.sbar) {
                return 'Not all fields in SBAR Handover Member have been checked';
            }
        }

        if (dataForm.dischargeDestination?.snomedCode === '19712007' && !dataForm.hospital) {
            return 'No hospital has been selected';
        }
        if (props.dialogType === 'dischargeEDAU' && dataForm.dischargeDestination?.snomedCode === '306706006' && !dataForm.ward) {
            return 'No ward has been selected';
        }

        if (
            (valid && (delayTime > 4 ? dataForm.delayReasons?.primaryDelayReason : true)) ||
            (props.isActionMode && dataForm.departureDate && dataForm.departureTime && dataForm.who)
        ) {
            return null;
        } else {
            return 'Not all required fields have been filled';
        }
    };

    const getNote = () => {
        return props.isActionMode
            ? Notes.getActionDischargeNote(dataForm)
            : Notes.getDischargeNote(
                  delayTime,
                  dataForm,
                  diagnosis,
                  triage?.treatmentAndManagementPlan,
                  triage?.investigations,
                  props.dialogType,
                  props.hideDiagTreatInv,
                  clinicalCourse,
                  dischargeGroup,
                  age,
                  dischargeTemplate,
              );
    };

    const calculateDelayTime = (data) => {
        const { departureDate, departureTime } = data;
        const delay = moment
            .duration(
                moment(`${moment(departureDate).format(ecdsDateFormat)} ${moment(departureTime).format(ecdsTimeFormat)}`).diff(
                    calculateArrivalDate(props.patient),
                ),
            )
            .asHours();

        setDelayTime(_.isNaN(delay) ? delayTime : delay);
    };

    const handleChange = (event, type) => {
        const { name, value } = event.target;

        if (type === 'delay') {
            setDataForm((formData) => ({
                ...formData,
                delayReasons: { ...formData.delayReasons, [name]: value },
            }));
        } else {
            setDataForm((formData) => ({
                ...formData,
                [name]: value,
            }));
        }
    };

    const handlePickerChange = (value, name) => {
        if (value) {
            setDataForm((formData) => ({
                ...formData,
                [name]: value,
            }));
        }
    };

    const setDataDictionary = (data, type) => {
        handleChange(
            {
                target: {
                    name: data[0],
                    value: data?.[1] || null,
                },
            },
            type,
        );
    };

    const openClerkingReviewModal = () => {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <AgyleTextField
                        disabled
                        label={t('Clerking examinations')}
                        value={props.patient?.clerking?.examination}
                        variant="outlined"
                        fullWidth
                        multiline
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        disabled
                        label={t('Clerking differential diagnosis / problem list')}
                        fullWidth
                        value={props.patient?.clerking?.workingDiagnosis}
                        multiline
                        variant="outlined"
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        disabled
                        label={t('Clerking management plan')}
                        fullWidth
                        value={props.patient?.clerking?.additionalInformation}
                        variant="outlined"
                        multiline
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        disabled
                        label={t('Clerking investigations results')}
                        fullWidth
                        value={props.patient?.clerking?.investigationsDetails}
                        variant="outlined"
                        multiline
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </Grid>
        );
    };

    const renderDelay = useCallback(() => {
        return (
            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center" sx={{ mt: 0, pl: 2, mb: 2 }}>
                <FormLabel>{t('Delay Reasons')}</FormLabel>

                <InformationBox type={'warning'}>{t('Patient waited more than 4 hours.')}</InformationBox>

                {selfDischardeAlertVisable && (
                    <InformationBox type={'warning'}>{t('Self discharge awaiting second signature')}</InformationBox>
                )}

                <InformationBox type={'info'}>
                    <AgyleButton color="primary" variant="contained" onClick={openModal}>
                        {t('Open Clerking Review')}
                    </AgyleButton>
                </InformationBox>

                <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                    <Grid item xs={12} md={6}>
                        <div className={classesLocal.input}>
                            <DelayReason
                                label={t('Primary Delay Reason')}
                                withBackground={false}
                                paperBackground
                                value={dataForm?.delayReasons?.primaryDelayReason || null}
                                onChange={(e) => setDataDictionary(['primaryDelayReason', e], 'delay')}
                                error={props?.isError && _.isEmpty(dataForm.delayReasons.primaryDelayReason)}
                                disabled={!!dataReadOnly?.delayReasons?.primaryDelayReason || isDisabled}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div className={classesLocal.input}>
                            <DelayReason
                                label={t('Secondary Delay Reason')}
                                withBackground={false}
                                paperBackground
                                value={dataForm.delayReasons.secondaryDelayReason || null}
                                onChange={(e) => setDataDictionary(['secondaryDelayReason', e], 'delay')}
                                disabled={
                                    dataForm?.delayReasons?.primaryDelayReason?.disableSecondaryReason ||
                                    !!dataReadOnly?.delayReasons?.secondaryDelayReason ||
                                    isDisabled
                                }
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            name="delayReasonText"
                            label={t('Delay Reason Text')}
                            onChange={(event) => handleChange(event, 'delay')}
                            value={dataForm.delayReasons.delayReasonText || ''}
                            multiline
                            fullWidth
                            rows={5}
                            disabled={!!dataReadOnly?.delayReasons?.delayReasonText || isDisabled}
                            readOnly={!!dataReadOnly.delayReasons && !!dataReadOnly.delayReasons.delayReasonText}
                            textFieldClass={classesLocal.picker}
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </Grid>
            </Grid>
        );
    }, [
        dataForm.delayReasons.delayReasonText,
        dataForm.delayReasons.primaryDelayReason,
        dataForm.delayReasons.secondaryDelayReason,
        props?.isError,
        isModalOpen,
        selfDischardeAlertVisable,
    ]);

    const renderDiagnosis = useCallback(() => {
        return (
            <Grid container spacing={2} direction="column" justifyContent="center" alignItems="flex-start">
                <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('Diagnosis, Treatment and Investigations')}
                    </Typography>
                </Grid>

                <Grid item xs={12} style={{ width: '100%' }}>
                    <ControlDiagnosis
                        label={t('Diagnosis')}
                        error={
                            props?.isError && _.isEmpty(diagnosis?.primary) && !props.patient?.metadata?.diagnosisNotRequired?.acknowledge
                        }
                        value={diagnosis?.primary ? diagnosis?.primary : []}
                        disableEditTable={isDisabled}
                        disabled={isDisabled}
                        agyleRefresh
                        onChange={(value) => {
                            let clone = _.cloneDeep(diagnosis);
                            if (!clone) {
                                clone = {};
                            }
                            clone.primary = value;
                            setDiagnosis(clone);
                        }}
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                <Grid item xs={12} style={{ width: '100%' }}>
                    <ControlTreatment
                        label={t('Treatment')}
                        error={
                            props?.isError &&
                            _.isEmpty(triage?.treatmentAndManagementPlan) &&
                            !props.patient?.metadata?.diagnosisNotRequired?.acknowledge
                        }
                        value={triage?.treatmentAndManagementPlan ? triage?.treatmentAndManagementPlan : []}
                        disableEditTable={isDisabled}
                        disabled={isDisabled}
                        color="primary"
                        agyleRefresh
                        onChange={(value) => {
                            setTriage((pr) => ({
                                ...pr,
                                treatmentAndManagementPlan: value,
                            }));
                        }}
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>

                <Grid item xs={12} style={{ width: '100%', marginBottom: 10 }}>
                    <ControlInvestigations
                        label={t('Investigations')}
                        error={
                            props?.isError &&
                            _.isEmpty(triage?.investigations) &&
                            !props.patient?.metadata?.diagnosisNotRequired?.acknowledge
                        }
                        value={triage?.investigations ? triage?.investigations : []}
                        disableEditTable={isDisabled}
                        disabled={isDisabled}
                        color="primary"
                        agyleRefresh
                        onChange={(value) =>
                            setTriage((pr) => ({
                                ...pr,
                                investigations: value,
                            }))
                        }
                        admitDate={props.patient?.admission?.startDate}
                        withBorder
                    />
                </Grid>
            </Grid>
        );
    }, [props.patient, props?.isError, diagnosis, triage]);

    const showWarningTime = () => {
        if (showMidnightWarningInDischarge && dataForm?.departureTime?.format('HHmm') === '0000') {
            return (
                <div style={{ marginLeft: 16, width: '100%' }}>
                    <InformationBox type="warning">
                        {t('To avoid confusion, if the time should not be recorded as 00:00, it should always be either 23:59 or 00:01')}
                    </InformationBox>
                </div>
            );
        }
        return null;
    };

    const renderDischargeClinician = () => {
        return (
            <>
                {showWarningTime()}
                <Grid item xs={12} md={6}>
                    <AgyleDatePicker
                        type="date"
                        name="departureDate"
                        label={t('Departure Date')}
                        value={dataForm?.departureDate || null}
                        onChange={(e) => handlePickerChange(e, 'departureDate')}
                        required
                        maxDate={isDischargeSaved ? null : moment()}
                        minDate={isDischargeSaved ? null : moment(props.patient?.admission?.startDate)}
                        readOnly={!!dataReadOnly.departureDate}
                        error={props?.isError && _.isEmpty(dataForm?.departureDate)}
                        onError={(event) =>
                            setErrorPicker((prevState) => ({
                                ...prevState,
                                date: event,
                            }))
                        }
                        disabled={isDisabled}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <AgyleDatePicker
                        type="time"
                        name="departureTime"
                        label={t('Departure Time')}
                        value={dataForm.departureTime || null}
                        onChange={(e) => handlePickerChange(e, 'departureTime')}
                        minTime={
                            moment(dataForm.departureDate).format('DD/MM/YYYY') === moment(arrivalDate).format('DD/MM/YYYY')
                                ? moment(arrivalDate)
                                : null
                        }
                        maxTime={
                            dataForm.departureDate
                                ? moment(dataForm.departureDate).format('L') === moment().format('L')
                                    ? moment()
                                    : null
                                : null
                        }
                        required
                        readOnly={!!dataReadOnly.departureTime}
                        error={props?.isError && _.isEmpty(dataForm.departureTime)}
                        onError={(event) =>
                            setErrorPicker((prevState) => ({
                                ...prevState,
                                time: event,
                            }))
                        }
                        disabled={isDisabled}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <div className={classesLocal.input}>
                        <ControlConsultantComponent
                            typeDict={'DischargeClinicians'}
                            required
                            error={props?.isError && _.isEmpty(dataForm.who)}
                            label={'Discharge Clinician'}
                            allowedConsultants={consultantDictionary.discharge}
                            value={dataForm.who}
                            disabled={!!dataReadOnly.who || isDisabled}
                            onChange={(event) => {
                                setDataForm((prevState) => ({ ...prevState, who: event }));
                            }}
                            withBackground={false}
                            paperBackground
                            size="large"
                            noBorder={false}
                            withNewBackground
                        />
                    </div>
                </Grid>
            </>
        );
    };

    const handleChangeNokContacted = (val) => {
        setDataForm((prevState) => ({
            ...prevState,
            nokContacted: val,
            nokContactedReason: '',
        }));
    };

    const handleOpenCalendar = () => {
        API.findPatientsInWard({ wardIds: mapObject?.wardsId, bedasStatus: 'started' })
            .then((res) => {
                if (res?.data?.__data) {
                    setEvents(res?.data?.__data);
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(
                    setSnackbar({
                        type: 'error',
                        message: 'Bed status was not saved successfully!',
                    }),
                );
            })
            .finally(() => openCalendar());
    };

    const handleDisableDischargeSubmitButton = (val) => {
        if (props.setFormDisableDischarge) props.setFormDisableDischarge(val);
    };

    const handleChangeSwitch = (val) => {
        setFormNotRequired(val);

        // for 'Not Required'
        if (_.isNull(val)) handleDisableDischargeSubmitButton(false);
        else handleDisableDischargeSubmitButton(!val);
    };

    const getCompletedFormsOutput = () => {
        if (dataForm.dischargeFollowUp?.snomedCode === fractureClinicCode) {
            if (formCheck) {
                return (
                    <div className={classesLocal.infoBoxForm}>
                        <InformationBox type="info">
                            {t('VFC form completed.')}
                            <br />
                            <span>
                                {`By ${formCheck?.__lastModifiedByFullName} (${formCheck?.__lastModifiedBy}) at ${moment(formCheck?.__lastModifiedDate).format(defaultDateTimeFormat)}`}
                            </span>
                        </InformationBox>
                    </div>
                );
            }

            return (
                <div className={classesLocal.infoBoxForm}>
                    <InformationBox type="error">{t('Discharge disabled - VFC form not submitted.')}</InformationBox>

                    <div className={classesLocal.divRow}>
                        <FormLabel style={{ marginRight: 10 }}>{t('VFC form required')}</FormLabel>

                        <AgyleBlockSwitch
                            onChange={handleChangeSwitch}
                            value={formNotRequired}
                            customLabels={{ yes: t('No'), no: t('Yes') }}
                        />
                    </div>
                </div>
            );
        } else if (dataForm.dischargeFollowUp?.snomedCode === physiotherapyCode) {
            return (
                <div className={clsx(classesLocal.infoBoxForm, classesLocal.divRow)}>
                    <FormLabel style={{ marginRight: 10 }}>{t('Has a Physiotherapy Referral Form been submitted?')}</FormLabel>

                    <AgyleBlockSwitch
                        onChange={handleChangeSwitch}
                        value={formNotRequired}
                        nullState
                        customLabels={{ empty: t('Not Required') }}
                    />
                </div>
            );
        }

        return null;
    };

    const renderDischarge = useCallback(() => {
        return (
            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center" sx={{ pl: 2 }}>
                <Typography variant={'subtitle1'} fontWeight="bold" style={{ margin: '10px 0' }}>
                    {t(
                        `Discharge From ${props.dialogType === 'dischargeEDAU' ? 'Emergency Department Assessment Unit' : 'Emergency Care'}`,
                    )}
                </Typography>
                <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                    {props.dialogType === 'dischargeEDAU' && (
                        <Grid item xs={12}>
                            <AgyleDatePicker
                                type="dateTime"
                                label={t('Clinical Conclusion Date and Time')}
                                error={props?.isError}
                                ampm={false}
                                value={dataForm.clinicalConclusion}
                                disabled={isDisabled}
                                readOnly={true}
                                textFieldClass={classesLocal.picker}
                            />
                        </Grid>
                    )}

                    {renderDischargeClinician()}

                    <Grid item xs={12}>
                        <div className={classesLocal.input}>
                            <DischargeStatus
                                name="dischargeStatus"
                                label={t('Discharge Status')}
                                error={props?.isError && _.isEmpty(dataForm.dischargeStatus)}
                                value={dataForm.dischargeStatus || null}
                                required
                                withBackground={false}
                                paperBackground
                                showAll
                                disabled={!!dataReadOnly.dischargeStatus || isDisabled}
                                onChange={(_e, val) => setDataDictionary(['dischargeStatus', val])}
                                admitDate={props.patient?.admission?.startDate}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <div className={classesLocal.input}>
                            <DischargeDestination
                                name="dischargeDestination"
                                label={t('Discharge Destination')}
                                error={props?.isError && _.isEmpty(dataForm.dischargeDestination)}
                                withBackground={false}
                                paperBackground
                                value={dataForm.dischargeDestination || null}
                                disabled={!!dataReadOnly.dischargeDestination || isDisabled}
                                onChange={(_e, val) => setDataDictionary(['dischargeDestination', val])}
                                required
                                getOptionSelected={(option, value) => option.__code === value.__code}
                                // getOptionLabel={(option) => option.__description}
                                admitDate={props.patient?.admission?.startDate}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </div>
                    </Grid>

                    {/* If Discharge Destination is set to transfer to another hospital */}
                    {dataForm.dischargeDestination?.snomedCode === '19712007' && (
                        <Grid item xs={12}>
                            <div className={classesLocal.input}>
                                <AgyleDictionaryField
                                    label={'Transfer to Hospital'}
                                    getOptionLabel={(option) => option.organizationName}
                                    options={hospitals}
                                    required
                                    error={props?.isError && _.isEmpty(dataForm.hospital)}
                                    value={dataForm.hospital || null}
                                    disabled={!!dataReadOnly.hospital || isDisabled}
                                    onChange={(event, newValue) =>
                                        setDataForm((prevState) => ({
                                            ...prevState,
                                            hospital: newValue,
                                        }))
                                    }
                                    withBackground={false}
                                    paperBackground
                                    size="large"
                                    noBorder={false}
                                    withNewBackground
                                />
                            </div>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <div className={classesLocal.input}>
                            <DischargeFollowUp
                                name="dischargeFollowUp"
                                label={t('Discharge Follow Up')}
                                error={props?.isError && _.isEmpty(dataForm.dischargeFollowUp)}
                                color="primary"
                                value={dataForm.dischargeFollowUp || null}
                                disabled={!!dataReadOnly.dischargeFollowUp || isDisabled}
                                onChange={(_e, val) => setDataDictionary(['dischargeFollowUp', val])}
                                required
                                agyleRefresh
                                getOptionSelected={(option, value) => option.__code === value.__code}
                                // getOptionLabel={(option) => option.__description}
                                withBackground={false}
                                paperBackground
                                admitDate={props.patient?.admission?.startDate}
                                size="large"
                                noBorder={false}
                                withNewBackground
                            />
                        </div>
                    </Grid>

                    {bedasEnabled && !schedulerEnabled && (
                        <>
                            <Grid item xs={12} sx={{ pl: 2 }}>
                                <div className={clsx(classesLocal.input, classesLocal.divRow)}>
                                    <Typography variant="subtitle1" fontWeight="bold" style={{ marginRight: 10 }}>
                                        {t('Follow Up')}
                                    </Typography>

                                    <AgyleBlockSwitch
                                        onChange={(val) => {
                                            setBedas(val);
                                            bedasTouch.current = true;
                                        }}
                                        value={bedas}
                                    />
                                    {bedas && (
                                        <AgyleButton
                                            onClick={handleOpenCalendar}
                                            style={{ marginLeft: 'auto' }}
                                            variant="contained"
                                            colour={!bedasDate ? 'error' : ''}
                                        >
                                            {t('Open Calendar')}
                                        </AgyleButton>
                                    )}
                                </div>
                            </Grid>
                            {bedasDate && (
                                <Grid item xs={12}>
                                    <AgyleDatePicker
                                        label={t('Follow Up Date')}
                                        value={bedasDate}
                                        onChange={setBedasDate}
                                        textFieldClass={classesLocal.picker}
                                    />
                                </Grid>
                            )}
                        </>
                    )}

                    {schedulerEnabled && (
                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <AgyleButton onClick={openScheduler} variant="contained">
                                {t('Open Scheduler')}
                            </AgyleButton>

                            {schedulerHour && (
                                <div className={classesLocal.divRow} style={{ gap: 5 }}>
                                    <Typography variant="subtitle1">{t('Scheduled Date and Time')}</Typography>

                                    <Typography variant="body1" fontWeight="bold">
                                        {moment(schedulerHour).format(defaultDateTimeFormat)}
                                    </Typography>
                                </div>
                            )}
                        </Grid>
                    )}

                    {nokQuestionEnabled && nokQuestionShow && (
                        <>
                            <Grid item xs={12} sx={{ pl: 2 }}>
                                <div className={clsx(classesLocal.input, classesLocal.divRow)}>
                                    <Typography variant="subtitle1" fontWeight="bold" style={{ marginRight: 10 }}>
                                        {t('Have the next of Kin been contacted?')}
                                    </Typography>

                                    <AgyleBlockSwitch
                                        onChange={handleChangeNokContacted}
                                        value={dataForm.nokContacted}
                                        nullState
                                        size="small"
                                        disabled={isDisabled}
                                    />

                                    {dataForm?.nokContacted === false && (
                                        <AgyleButton onClick={handleOpenNokReason} style={{ marginLeft: 'auto' }} variant="contained">
                                            {t('Reason & Actions')}
                                        </AgyleButton>
                                    )}
                                </div>
                            </Grid>

                            {dataForm?.nokContacted === false && (
                                <Grid item xs={12}>
                                    <InformationBox type="info">
                                        {t('Before the discharge, please contact the next of kin or provide a reason and action taken')}
                                    </InformationBox>
                                </Grid>
                            )}
                        </>
                    )}

                    {formCheckEnabled &&
                        (dataForm.dischargeFollowUp?.snomedCode === fractureClinicCode ||
                            dataForm.dischargeFollowUp?.snomedCode === physiotherapyCode) && (
                            <>
                                {loadingForms && (
                                    <AgyleLoadingSpinner
                                        className={classesLocal.center}
                                        label={t('Checking completed forms. Please wait.')}
                                    />
                                )}
                                {!loadingForms && getCompletedFormsOutput()}
                            </>
                        )}
                </Grid>
            </Grid>
        );
    }, [
        dataForm.clinicalConclusion,
        dataForm.departureDate,
        dataForm.departureTime,
        dataForm.who,
        dataForm.dischargeStatus,
        dataForm.dischargeDestination,
        dataForm.dischargeFollowUp,
        dataForm.hospital,
        dataForm.ward,
        dataForm.dischargeOther,
        dataForm.nokContacted,
        dataForm.nokContactedReason,
        props?.isError,
        nokQuestionShow,
        bedas,
        bedasDate,
        loadingForms,
        formCheck,
        formNotRequired,
        schedulerHour,
    ]);

    const accordionChangeHandler = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const isPaedsPatient = (dob) => {
        return moment().diff(dob, 'years') < 18;
    };

    const checkSafeguardingLevel = () => {
        return checkSafeguardingLevelForm(props.patient) && !props.disableSave && !props.seeAndTreat;
    };

    const checkSafeguardingForm = () => {
        return (
            safeguardingFormModalInDis &&
            !props.disableSave &&
            isPaedsPatient(props.patient.patient.dob) &&
            getSafeguardingLevel(props.patient) &&
            getSafeguardingLevel(props.patient) !== 'green' &&
            !props.patient.studies?.safeguardingFormStatus
        );
    };

    const handleSelectBedasDate = useCallback((event, edReviewCtx = false) => {
        if (edReviewCtx) {
            const { sessionId, reviewReason, reviewDate } = event;

            setBedasDate(reviewDate);
            setBedasSessionId(sessionId);
            setBedasReason(reviewReason);
            closeCalendar();
        } else {
            setBedasDate(event);
            openBooking();
        }
    }, []);

    const handleOpenNokReason = () => {
        setNokReason(dataForm?.nokContactedReason || '');
        openNoKReason();
    };

    const handleBookingBedas = () => {
        closeBooking();
        closeCalendar();
    };

    const handleChangeNokReason = () => {
        setDataForm((prevState) => ({
            ...prevState,
            nokContactedReason: nokReason,
        }));

        handleCloseNokReason();
    };

    const handleChangeFlags = (event, name) => {
        const checked = event.target.value === 'true';

        firstUpdateFlag.current = false;
        let data;

        if (!firstUpdateFlag.current) firstUpdateFlag.current = true;

        data = setOptional(flags, name, checked);
        setFlags((prevState) => ({ ...prevState, ...data }));
    };

    const setOptional = (flags, flag, value) => {
        let data = {
            maj: flags.maj,
            res: flags.res,
            amb: flags.amb,
            min: flags.min,
            utc: flags.utc,
            rats: flags.rats,
            children: flags.children,
        };
        if (value) {
            data.maj = data.res = data.amb = data.min = data.utc = data.rats = data.children = false;
        }
        data[flag] = value;
        return data;
    };

    const handleCloseBooking = () => {
        setBedasDate(null);
        closeBooking();
    };

    const handleCloseNokReason = () => {
        setNokReason('');
        closeNoKReason();
    };

    const openLink = () => {
        window.open(safeguardingFormLink);
    };

    const saveSafeFormStatus = (value) => {
        if (props.saveSafeFormStatus) props.saveSafeFormStatus(value);
    };

    const handleCFSNotRequired = () => {
        const data = {
            acknowledge: true,
            timestamp: moment().toISOString(),
            user: userFullName,
            userLogin: userObject?.userName,
        };

        saveSafeFormStatus([
            {
                name: 'metadata.CFSNotRequired',
                value: data,
                noteToAdd: Notes.getCFSNotRequired(data),
            },
        ]);
        closeWarning();
    };

    const handleOpenMandatoryForm = (workflowId) => {
        dispatch(setWorkflow({ type: workflowId }));
        closeWarning();
        if (props.closeDischarge) props.closeDischarge();
        if (props._onForceClose) {
            dispatch(closePatientForm('discharge'));
        }
    };

    const [dialogDetails, setDialogDetails] = React.useState({
        onClose: openWarning,
        title: 'title',
        buttons: [
            {
                title: 'button1',
                event: () => {},
                workflowDisabled: true,
            },
            {
                title: 'button2',
                event: () => {},
                workflowDisabled: false,
            },
        ],
    });

    useEffect(() => {
        const { seeAndTreat } = props;
        const isIP = patientType === PATIENT_CONTEXT_TYPE.INPATIENT;

        if (!isIP && !props.patient?.triage?.discriminator && triageMandatoryToDischarge && !seeAndTreat) {
            setDialogDetails({
                onClose: closeWarning,
                title: 'PLEASE COMPLETE TRIAGE',
                buttons: [
                    {
                        title: 'Close',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        } else if (!isIP && checkSafeguardingLevel()) {
            setDialogDetails({
                onClose: closeWarning,
                title: 'PLEASE COMPLETE PAEDIATRIC SAFEGUARDING',
                buttons: [
                    {
                        title: 'Open form',
                        event: () => handleOpenMandatoryForm(WORKFLOW_OPTIONS.SAFEGUARDING),
                        workflowDisabled: props.workflowDisabled,
                    },
                    {
                        title: 'Close',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        } else if (!isIP && !props.disableSave && checkCFSForm(props.patient)) {
            setDialogDetails({
                onClose: closeWarning,
                title: 'PLEASE COMPLETE CLINICAL FRAILTY SCORE FORM',
                buttons: [
                    {
                        title: 'Form not required',
                        event: () => handleCFSNotRequired(),
                        workflowDisabled: false,
                    },
                    {
                        title: 'Open form',
                        event: () => handleOpenMandatoryForm(WORKFLOW_OPTIONS.CLINICAL_FRAILTY_SCORE),
                        workflowDisabled: props.workflowDisabled,
                    },
                    {
                        title: 'Close',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        } else if (
            !props.patient?.studies?.verificationOfDeath &&
            verificationOfDeathAgyleMiniForm &&
            props.patient?.clinicalConclusion?.status === 'Deceased'
        ) {
            setDialogDetails({
                onClose: closeWarning,
                title: 'PLEASE COMPLETE VERIFICATION OF DEATH FORM',
                buttons: [
                    {
                        title: 'Open Verification of death form',
                        event: () => handleOpenMandatoryForm(WORKFLOW_OPTIONS.VERIFICATION_OF_DEATH),
                        workflowDisabled: props.workflowDisabled,
                    },
                    {
                        title: 'Close',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        } else if (!isIP && checkSafeguardingForm()) {
            setDialogDetails({
                onClose: closeWarning,
                title: 'PLEASE COMPLETE SAFEGURADING FOR CHILDREN FORM',
                buttons: [
                    {
                        title: 'Acknowledged',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                            closeWarning();
                        },
                        workflowDisabled: false,
                    },
                    {
                        title: 'Open Link',
                        event: () => {
                            openLink();
                        },
                        workflowDisabled: false,
                    },
                    {
                        title: 'Form already completed',
                        event: () => {
                            saveSafeFormStatus([
                                {
                                    name: 'studies.safeguardingFormStatus',
                                    value: true,
                                    noteToAdd: Notes.getSafeguardingFormNote(),
                                },
                            ]);
                            closeWarning();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        } else if (checkCauseForConcernForm(props.patient)) {
            setDialogDetails({
                onClose: closeWarning,
                title: `Please consider filling out Cause for Concern for Adults Referral`,
                buttons: [
                    {
                        title: 'Acknowledged',
                        event: () => {
                            if (props.closeDischarge) props.closeDischarge();
                            if (props._onForceClose) props._onForceClose();
                            closeWarning();
                        },
                        workflowDisabled: false,
                    },
                    {
                        title: 'Open Link',
                        event: () => {
                            window.open(causeForConcernFormLink);
                        },
                    },
                    {
                        title: 'Form already completed',
                        event: () => {
                            saveSafeFormStatus([
                                {
                                    name: 'studies.causeForConcernForm',
                                    value: true,
                                    noteToAdd: Notes.getCauseForConcernFormNote(),
                                },
                            ]);
                            closeWarning();
                        },
                        workflowDisabled: false,
                    },
                ],
            });
            openWarning();
        }
    }, [props.patient, patientType]);

    const renderDialog = () => {
        return (
            <Dialog open={isWarning} onClose={closeWarning} disableEscapeKeyDown fullWidth={true}>
                <DialogContent dividers>
                    <InformationBox type={'warning'}>{dialogDetails.title}</InformationBox>
                </DialogContent>
                <DialogActions className={classesLocal.dialogRoot}>
                    {dialogDetails.buttons.map((button, index) => {
                        if (!button.workflowDisabled) {
                            return (
                                <Button key={'button' + index} color="tertiary" onClick={() => button.event()}>
                                    {button.title}
                                </Button>
                            );
                        }
                    })}
                </DialogActions>
            </Dialog>
        );
    };

    const renderDisease = () => {
        return (
            <AgyleAccordion
                expanded={expanded === 'disease'}
                onChange={accordionChangeHandler('disease')}
                label={t('Research and Disease in discharge Outbreak Notification')}
                formBackground
            >
                <DiseaseOutbreak setData={setOutbreak} patient={props.patient} disabled={isDisabled} />
            </AgyleAccordion>
        );
    };

    const renderDischargeForm = (isActionMode) => {
        return (
            <>
                {isModalOpen && (
                    <DraggableModal bounds={noteTextBounds} width={800} nodeRef={noteTextRef} onClose={closeModal}>
                        {openClerkingReviewModal()}
                    </DraggableModal>
                )}

                {/* Display delay reasons*/}
                {!isActionMode && delayTime > 4 && renderDelay()}

                {!props.hideDiagTreatInv && !isActionMode && renderDiagnosis()}

                {renderDischarge()}

                {!isActionMode && (
                    <>
                        {renderDialog()}
                        <Divider style={{ margin: '10px 0' }} />

                        {schedulerEnabled && (
                            <AppointmentsAccordion expanded={expanded} onChange={accordionChangeHandler} patient={props.patient} />
                        )}

                        {diseaseInDischarge && renderDisease()}

                        <AgyleAccordion
                            expanded={expanded === 'messageToGP'}
                            onChange={accordionChangeHandler('messageToGP')}
                            label={t(props.dialogType !== 'dischargeEDAU' ? 'Message to GP / Plan & Advice Given' : 'Plan & Advice Given')}
                            formBackground
                        >
                            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                                <Grid item xs={12}>
                                    <AgyleSelectField
                                        label={t('Status')}
                                        value={clinicalCourse.actionsGivenStatus || ''}
                                        onChange={(event) => {
                                            setClinicalCourse((formData) => ({
                                                ...formData,
                                                actionsGivenStatus: event,
                                            }));
                                        }}
                                        withBackground
                                        shrink
                                        options={CLINICAL_COURSE_OPTIONS}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <AgyleTextField
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        rows={5}
                                        fullWidth
                                        multiline
                                        style={{ width: '100%' }}
                                        label={t('Plan and Requested Actions Given')}
                                        defaultValue={clinicalCourse?.actionsGiven}
                                        disabled={isDisabled}
                                        value={clinicalCourse?.actionsGiven}
                                        onChange={(event) => {
                                            setClinicalCourse((formData) => ({
                                                ...formData,
                                                actionsGiven: event.target.value,
                                            }));
                                        }}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <AgyleTextField
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        rows={5}
                                        fullWidth
                                        multiline
                                        style={{ width: '100%' }}
                                        label={t('Information and Advice Given')}
                                        defaultValue={clinicalCourse?.adviceGiven}
                                        disabled={isDisabled}
                                        value={clinicalCourse?.adviceGiven}
                                        onChange={(event) => {
                                            setClinicalCourse((formData) => ({
                                                ...formData,
                                                adviceGiven: event.target.value,
                                            }));
                                        }}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>

                                {props.dialogType !== 'dischargeEDAU' && (
                                    <Grid item xs={12}>
                                        <AgyleTextField
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            rows={5}
                                            fullWidth
                                            multiline
                                            style={{ width: '100%' }}
                                            label={t('Message to GP')}
                                            defaultValue={clinicalCourse?.clinicalCourse}
                                            disabled={isDisabled}
                                            value={clinicalCourse?.clinicalCourse}
                                            onChange={(event) => {
                                                setClinicalCourse((formData) => ({
                                                    ...formData,
                                                    clinicalCourse: event.target.value,
                                                }));
                                            }}
                                            size="large"
                                            noBorder={false}
                                            withNewBackground
                                        />
                                    </Grid>
                                )}

                                {props.patient?.studies?.clinicalCourseAuthor && (
                                    <Grid item xs={12}>
                                        <span className={classesLocal.clinicalCourseAuthor}>
                                            {t(`Created by ${props.patient?.studies?.clinicalCourse?.who} at 
                                                ${moment(props.patient?.studies?.clinicalCourse?.when).format(defaultDateTimeFormat)}`)}
                                        </span>
                                    </Grid>
                                )}
                            </Grid>
                        </AgyleAccordion>

                        <AgyleAccordion
                            expanded={expanded === 'safeguarding'}
                            onChange={accordionChangeHandler('safeguarding')}
                            label={t('Safeguarding')}
                            formBackground
                        >
                            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="center">
                                <Grid item xs={12}>
                                    <ChiefOfComplaint
                                        label={t('Chief Complaint')}
                                        disabled
                                        agyleRefresh
                                        value={props.patient?.triage?.chiefComplaint}
                                        admitDate={props.patient?.admission?.startDate}
                                        size="large"
                                        noBorder={false}
                                        withNewBackground
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Safeguarding
                                        label={t('Safeguarding')}
                                        color="primary"
                                        agyleRefresh
                                        value={safeguardingList}
                                        onChange={setSafeguardingList}
                                        disabled={isDisabled}
                                        admitDate={props.patient?.admission?.startDate}
                                        withBorder
                                    />
                                </Grid>

                                {age < 18 && (
                                    <Grid item xs={12} className={classesLocal.divRow}>
                                        <AgyleBlockSwitch
                                            disabled={!!dataReadOnly.edSisters || isDisabled}
                                            onChange={(event) =>
                                                setDataForm((prevState) => ({
                                                    ...prevState,
                                                    edSisters: event,
                                                }))
                                            }
                                            value={dataForm.edSisters}
                                        />
                                        <Typography variant="body1" style={{ marginLeft: 10 }}>
                                            {t('Please select to exclude safeguarding referral details in the EDS/EDN.')}
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item xs={12} className={classesLocal.divRow}>
                                    <AgyleBlockSwitch
                                        disabled={!!dataReadOnly.chadOrCamhs || isDisabled}
                                        onChange={(event) =>
                                            setDataForm((prevState) => ({
                                                ...prevState,
                                                chadOrCamhs: event,
                                            }))
                                        }
                                        value={dataForm.chadOrCamhs}
                                    />
                                    <Typography variant="body1" style={{ marginLeft: 10 }}>
                                        {t(
                                            'Please select if a referral was made to ChAD, CAMHS, or social services for this attendance. This will send the attendance details to the Safeguarding Office.',
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AgyleAccordion>

                        <AgyleAccordion
                            expanded={expanded === 'flags'}
                            onChange={accordionChangeHandler('flags')}
                            label={t('Validated Location')}
                            formBackground
                        >
                            <Grid container spacing={2} alignItems="center">
                                {!isFlagDisabled('res') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('Resus')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.res}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'res')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('maj') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('Major')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.maj}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'maj')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('amb') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('ACA')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.amb}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'amb')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('min') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('Minor')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.min}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'min')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('rats') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('RATs')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.rats}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'rats')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('utc') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('UTC')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.utc}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'utc')}
                                        />
                                    </Grid>
                                )}

                                {!isFlagDisabled('children') && (
                                    <Grid item xs={6} className={classesLocal.flagContainer}>
                                        <span className={classesLocal.flagTitle}>{t('Children')}</span>

                                        <AgyleBlockSwitch
                                            value={flags.children}
                                            disabled={isDisabled}
                                            onChange={(_, event) => handleChangeFlags(event, 'children')}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </AgyleAccordion>
                    </>
                )}
            </>
        );
    };

    const renderBookingMessage = () => {
        return (
            <Grid container spacing={1}>
                <Grid item xs={12} style={{ fontWeight: 'bold', marginBottom: 10 }}>
                    {t('Are you sure you want to book this date for this patient?')}
                </Grid>

                <Grid item xs={12}>
                    <AgyleDatePicker label={t('Follow Up Date')} value={bedasDate} onChange={setBedasDate} />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        onChange={(e) => setBedasReason(e.target.value)}
                        label={t('Reason')}
                        value={bedasReason}
                        fullWidth
                        multiline
                    />
                </Grid>
            </Grid>
        );
    };

    const renderNokReason = () => {
        const handleChange = (e) => {
            const { value } = e.target;
            setNokReason(value);
        };

        return (
            <Grid container spacing={1}>
                <Grid item xs={12} style={{ fontWeight: 'bold', marginBottom: 10 }}>
                    {t('Please provide a reason and action taken.')}
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        onChange={handleChange}
                        value={dataForm?.nokContactedReason}
                        fullWidth
                        multiline
                        debounce
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {renderDischargeForm(props.isActionMode)}

            {isCalendarOpen && schedulerEnabled && (
                <EdReviewDischarge
                    open={isCalendarOpen}
                    onClose={closeCalendar}
                    currentPatientInWard={props?.patient}
                    onSelectDate={handleSelectBedasDate}
                />
            )}

            {!schedulerEnabled && (
                <AgyleSwitchCalendar
                    calendarType="bedas"
                    events={events}
                    handleSelect={handleSelectBedasDate}
                    dischargeForm
                    open={isCalendarOpen}
                    onClose={closeCalendar}
                />
            )}

            {isSchedulerOpen && schedulerEnabled && (
                <SchedulerModal
                    open={isSchedulerOpen}
                    onClose={closeScheduler}
                    patient={props.patient}
                    setSchedulerHour={setSchedulerHour}
                />
            )}

            <MessageBox
                open={isBookingOpen}
                onClose={handleCloseBooking}
                title={t('Booking Patient')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                disableSave
                disabled={!bedasReason || !bedasDate}
                onAccept={handleBookingBedas}
                messageObject={renderBookingMessage()}
                width="sm"
            />

            <MessageBox
                open={isBedRequestInfoOpen}
                onClose={closeBedRequestInfo}
                title={t('Active Bed Request')}
                haveAccept={{ yes: t('Confirm') }}
                disableSave
                onAccept={closeBedRequestInfo}
                message={t(
                    'A patient has an active bed request, when this patient is discharged, the bed request status will be automatically changed to cancelled.',
                )}
                width="sm"
                warning
            />

            <MessageBox
                open={isNoKReasonOpen}
                onClose={handleCloseNokReason}
                title={t('Next of Kin Reason & Actions')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleChangeNokReason}
                messageObject={renderNokReason()}
                width="sm"
                disabled={isDisabled}
                disableSave
            />
        </>
    );
};

export default DischargeForm;
